// SafetyAuditForm.js
// This is the main form component for the Safety Audit form

import React, { useState } from "react";
import Select, { createFilter } from "react-select";
import { useNavigate } from "react-router-dom";
import { shallow } from "zustand/shallow";
import { useAppStore } from "../../store/appStore";
// User
import { useUserState } from "../../context/user";
// Components
import { DelDatePicker } from "../common/DelDatePicker";
import { TimePickerInput } from "../common/TimePickerInput";
import VirtualizedList from "../common/virtualList";
import { PhotosComponent } from "../common/PhotosComponent";
import ConfirmationModal from "../common/ConfirmationModal";
import DelSearchForAssociation from "./components/DelSearchForAssociation";
import SafetyAuditJsaTab from "./components/SafetyAuditJsaTab";
import {
  BackToTableButton,
  SubmitSafetyFormButton,
} from "../landingPage/components/buttons/SafetyFormActions";
// Autosave Indicator
import { AutosaveIndicator } from "../common/AutosaveIndicator";
import CurrentDateTimeButton from "../common/CurrentDateTimeButton";
// Hooks
import useSafetyAuditAutosave from "../../hooks/useSafetyAuditAutosave";
// Styles
import styles from "./SafetyAuditForm.module.css";
import { delSelect } from "../common/delSelect";

// Compute touch-aware React Select props
const useTouchAwareSelectProps = () => {
  const isTouch = useAppStore((s) => s.landingPage?.isTouch);
  const portalTarget =
    typeof document !== "undefined" ? document.body : undefined;
  if (isTouch) {
    return {
      menuPosition: "absolute",
      menuPlacement: "auto",
      menuShouldScrollIntoView: false,
      menuPortalTarget: portalTarget,
      styles: {
        menu: (base) => ({ ...base, zIndex: 9999 }),
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
      },
    };
  }
  return {
    menuPosition: "fixed",
    menuPortalTarget: portalTarget,
    styles: {
      menu: (base) => ({ ...base, zIndex: 9999 }),
      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    },
  };
};

export const SafetyAuditForm = ({
  currentSafetyAudit,
  safetyAuditMode,
  safetyAuditFormUI,
  jsaFormUI,
  photosUI,
  rev,
  safetyAuditFormActions,
  setShowSafetyAuditForm,
  autosaveErrors,
  autosaveErrorsActions,
  refreshListFunction = null,
  returnPath = null, // Optional: path to navigate back to (for reporting tab)
}) => {
  const navigate = useNavigate();
  const user = useUserState().currentUser;
  const touchSelectProps = useTouchAwareSelectProps();

  // Get all projects and employees from the store
  const allProjectInfo = useAppStore((s) => s.projects.list, shallow);
  const allInactiveProjects = useAppStore(
    (s) => s.projects.inactiveList,
    shallow
  );
  const employees = useAppStore((s) => s.employees.list, shallow);
  const allTerminatedEmployees = useAppStore(
    (s) => s.employees.terminatedList,
    shallow
  );
  const allSearchEmployees = [...employees, ...allTerminatedEmployees];

  // Loading and notification actions
  const showLoading = useAppStore((s) => s.loadingActions.showLoading);
  const hideLoading = useAppStore((s) => s.loadingActions.hideLoading);
  const { showNotification } = useAppStore(
    (s) => s.notificationActions,
    shallow
  );

  // Workflow actions
  const { submitSafetyAudit } = useAppStore(
    (s) => s.safetyFormWorkflowActions,
    shallow
  );

  // WebSocket autosave hook - only active when editing/creating
  // Pass autosaveErrorsActions for field-level error handling
  const shouldAutosave =
    safetyAuditMode === "new" || safetyAuditMode === "edit";
  const autosave = useSafetyAuditAutosave(
    shouldAutosave ? currentSafetyAudit?.id : null,
    autosaveErrorsActions
  );
  const { emitPatch } = autosave;

  // State for unsaved photos confirmation modal
  const [showUnsavedPhotosConfirm, setShowUnsavedPhotosConfirm] = useState(false);

  // Key to force TimePickerInput re-render when autofilled
  const [timeKey, setTimeKey] = useState(0);

  // Handle back to table
  const handleBackToTable = () => {
    const target = returnPath || "/home/safety_audit";
    navigate(target);
    setShowSafetyAuditForm(false);
    refreshListFunction?.();
  };

  // Core submit logic (called directly or after unsaved photos confirmation)
  const executeSubmitSafetyAudit = async () => {
    showLoading("Submitting safety audit...");
    const res = await submitSafetyAudit(currentSafetyAudit, user);
    hideLoading();
    if (res?.ok) {
      showNotification({
        type: "success",
        title: "Safety Audit Submitted",
        message: "The safety audit has been submitted successfully.",
      });
      handleBackToTable();
    } else if (res?.safetyAuditErrors) {
      showNotification({
        type: "validation",
        title: "Safety Audit Validation Errors",
        errors: res.safetyAuditErrors,
      });
    }
  };

  // Handle submit
  const handleSubmitSafetyAudit = async () => {
    // Check for unsaved selected photos (last guard)
    const selectedPhotos = photosUI?.selectedPhotos || [];
    if (selectedPhotos.length > 0) {
      setShowUnsavedPhotosConfirm(true);
      return;
    }

    await executeSubmitSafetyAudit();
  };

  // Helper to get autosave field error for safety audit fields
  const getAutosaveFieldError = (fieldName) => {
    const key = `safety_audit_main.${fieldName}`;
    return autosaveErrors?.fieldErrors?.[key] || null;
  };

  // Get submission errors and autosave errors
  const submissionErrors = currentSafetyAudit.errors || {};

  // Get autosave field errors for main fields
  const auditDateAutosaveError = getAutosaveFieldError("audit_date");
  const auditTimeAutosaveError = getAutosaveFieldError("audit_time");
  const projectNumberAutosaveError = getAutosaveFieldError("project_number");
  const supervisorAutosaveError = getAutosaveFieldError("supervisor");
  const scopeOfWorkAutosaveError = getAutosaveFieldError("scope_of_work");
  const pwCommentsAutosaveError = getAutosaveFieldError("pw_comments");
  const equipmentOnSiteAutosaveError = getAutosaveFieldError("equipment_on_site");
  const hazardsPresentAutosaveError = getAutosaveFieldError("hazards_present");
  const engineeringControlsAutosaveError = getAutosaveFieldError("engineering_controls");
  const ppeUsedAutosaveError = getAutosaveFieldError("ppe_used");
  const certsObtainedAutosaveError = getAutosaveFieldError("certs_obtained");
  const properlyTrainedCommentsAutosaveError = getAutosaveFieldError("properly_trained_comments");
  const positiveFindingsAutosaveError = getAutosaveFieldError("positive_findings");
  const negativeFindingsAutosaveError = getAutosaveFieldError("negative_findings");

  // Combine errors (autosave errors take precedence as they're more recent)
  const auditDateError = (auditDateAutosaveError || submissionErrors.audit_date) ? styles.error : "";
  const auditTimeError = (auditTimeAutosaveError || submissionErrors.audit_time) ? styles.error : "";
  const projectNumberError = (projectNumberAutosaveError || submissionErrors.project_number)
    ? styles.errorSelectContainer
    : "";
  const supervisorError = (supervisorAutosaveError || submissionErrors.supervisor) ? styles.errorSelectContainer : "";
  const scopeOfWorkError = (scopeOfWorkAutosaveError || submissionErrors.scope_of_work) ? styles.error : "";

  // Handle employees errors - can be a string or array
  const employeesError = submissionErrors.employees
    ? Array.isArray(submissionErrors.employees) && submissionErrors.employees.length > 0
      ? typeof submissionErrors.employees[0] === "string"
        ? submissionErrors.employees[0]
        : "Please fix employee errors below"
      : submissionErrors.employees
    : null;

  // Loading state
  if (!currentSafetyAudit) {
    return <div>Loading safety audit...</div>;
  }

  // Virtualized MenuList for Select components
  const MenuList = ({ children, maxHeight }) => {
    const itemCount = Array.isArray(children) ? children.length : 1;
    const itemHeight = 42;
    const listHeight = Math.min(maxHeight, itemCount * itemHeight);
    return (
      <VirtualizedList
        items={Array.isArray(children) ? children : [children]}
        height={listHeight}
        width="100%"
        itemHeight={itemHeight}
        renderItem={(child) => child}
      />
    );
  };

  return (
    <div className={styles.safetyAuditFormContainer}>
      {/* Tab Bar - hidden during DEL search */}
      {safetyAuditFormUI.activeTab !== "findDel" && (
        <div className={styles.tabBar}>
          <button
            className={`${styles.tabButton} ${
              safetyAuditFormUI.activeTab === "form" ? styles.activeTab : ""
            }`}
            onClick={() => safetyAuditFormActions.setActiveTab("form")}
            type="button"
          >
            Audit
          </button>
          {currentSafetyAudit.hasAssociatedJsa() && (
            <button
              className={`${styles.tabButton} ${
                safetyAuditFormUI.activeTab === "jsa" ? styles.activeTab : ""
              }`}
              onClick={() => safetyAuditFormActions.setActiveTab("jsa")}
              type="button"
            >
              JSA
            </button>
          )}
          <button
            className={`${styles.tabButton} ${
              safetyAuditFormUI.activeTab === "photos" ? styles.activeTab : ""
            }`}
            onClick={() => safetyAuditFormActions.setActiveTab("photos")}
            type="button"
          >
            Photos{" "}
            {currentSafetyAudit.photos?.length > 0 &&
              `(${currentSafetyAudit.photos.length})`}
          </button>
        </div>
      )}

      {/* Tab Content */}
      {safetyAuditFormUI.activeTab === "findDel" ? (
        <DelSearchForAssociation
          delSearchResults={safetyAuditFormUI.delSearchResults}
          delSearchTotalCount={safetyAuditFormUI.delSearchTotalCount}
          delSearchCurrentPage={safetyAuditFormUI.delSearchCurrentPage}
          delSearchPageSize={safetyAuditFormUI.delSearchPageSize}
          delSearchSelectedDelId={safetyAuditFormUI.delSearchSelectedDelId}
          delSearchLoading={safetyAuditFormUI.delSearchLoading}
          delSearchFilters={safetyAuditFormUI.delSearchFilters}
          delSearchFilterPhaseList={safetyAuditFormUI.delSearchFilterPhaseList}
          delSearchShowFilterMenu={safetyAuditFormUI.delSearchShowFilterMenu}
          safetyAuditFormActions={safetyAuditFormActions}
          showLoading={showLoading}
          hideLoading={hideLoading}
          showNotification={showNotification}
          allProjectInfo={allProjectInfo}
          allInactiveProjects={allInactiveProjects}
          allEmployees={allSearchEmployees}
        />
      ) : safetyAuditFormUI.activeTab === "jsa" ? (
        <SafetyAuditJsaTab
          currentSafetyAudit={currentSafetyAudit}
          jsaLoading={safetyAuditFormUI.jsaLoading}
          jsaFormUI={jsaFormUI}
          jsaActions={safetyAuditFormActions.jsa}
        />
      ) : safetyAuditFormUI.activeTab === "form" ? (
      <div className={styles.safetyAuditFormBody}>
        <div className={styles.formContentWrapper}>
          {/* Audit Information Section */}
          <div className={styles.formSection}>
            <div className={styles.sectionTitleRow}>
              <h2 className={styles.sectionTitle}>Audit Information</h2>
              {safetyAuditMode !== "view" && (
                <div className={styles.delAssociationButtons}>
                  {currentSafetyAudit.hasAssociatedDel() ? (
                    <>
                      <span className={styles.associatedDelLabel}>
                        DEL linked
                      </span>
                      <button
                        type="button"
                        className={styles.removeDelButton}
                        onClick={() => {
                          if (window.confirm("Remove the associated DEL? This will clear the project information and all employees.")) {
                            safetyAuditFormActions.handleDisassociateDel(showLoading, hideLoading, showNotification);
                          }
                        }}
                      >
                        Remove DEL
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className={styles.findDelButton}
                      onClick={() => safetyAuditFormActions.showDelSearch()}
                    >
                      Find DEL
                    </button>
                  )}
                </div>
              )}
            </div>
            <div className={styles.dateTimeRow}>
              <div className={styles.formField}>
                <label htmlFor="auditDate">Audit Date</label>
                <DelDatePicker
                  id="auditDate"
                  selected={currentSafetyAudit.audit_date || null}
                  onChange={(date) =>
                    safetyAuditFormActions.updateAuditDate(date, emitPatch)
                  }
                  className={`${styles.dateInput} ${auditDateError}`}
                />
                {(auditDateAutosaveError || submissionErrors.audit_date) && (
                  <span className={styles.errorText}>
                    {auditDateAutosaveError || submissionErrors.audit_date}
                  </span>
                )}
              </div>
              <div className={styles.formField}>
                <label htmlFor="auditTime">Audit Time</label>
                <TimePickerInput
                  key={timeKey}
                  id="auditTime"
                  interval={1}
                  value={currentSafetyAudit.audit_time || ""}
                  onChange={(time) =>
                    safetyAuditFormActions.updateAuditTime(time, emitPatch)
                  }
                  className={`${styles.timeInput} ${auditTimeError}`}
                />
                {(auditTimeAutosaveError || submissionErrors.audit_time) && (
                  <span className={styles.errorText}>
                    {auditTimeAutosaveError || submissionErrors.audit_time}
                  </span>
                )}
              </div>
              <CurrentDateTimeButton
                onDateChange={(date) =>
                  safetyAuditFormActions.updateAuditDate(date, emitPatch)
                }
                onTimeChange={(time) => {
                  safetyAuditFormActions.updateAuditTime(time, emitPatch);
                  setTimeKey((k) => k + 1);
                }}
                disabled={safetyAuditMode === "view"}
              />
            </div>
            <div className={styles.formGrid}>
              <div className={styles.formField}>
                <label htmlFor="projectNumber">Project</label>
                <Select
                  id="projectNumber"
                  classNamePrefix="project-search-input"
                  value={
                    currentSafetyAudit.project_number
                      ? {
                          value: currentSafetyAudit.project_number,
                          label: `${currentSafetyAudit.project_number} - ${currentSafetyAudit.project_name}`,
                          project_name: currentSafetyAudit.project_name,
                        }
                      : null
                  }
                  onChange={(selected) =>
                    safetyAuditFormActions.updateProjectNumber(
                      selected,
                      emitPatch
                    )
                  }
                  options={allProjectInfo}
                  isSearchable={true}
                  isClearable={true}
                  getOptionValue={(option) => option.value}
                  getOptionLabel={(option) => option.label}
                  placeholder={
                    (projectNumberAutosaveError || submissionErrors.project_number) ? (
                      <span className={styles.errorText}>
                        {projectNumberAutosaveError || submissionErrors.project_number}
                      </span>
                    ) : (
                      "Search projects..."
                    )
                  }
                  styles={{
                    ...delSelect,
                    ...(touchSelectProps.styles || {}),
                  }}
                  menuPortalTarget={touchSelectProps.menuPortalTarget}
                  menuPosition={touchSelectProps.menuPosition}
                  menuPlacement={touchSelectProps.menuPlacement}
                  menuShouldScrollIntoView={
                    touchSelectProps.menuShouldScrollIntoView
                  }
                  isDisabled={safetyAuditMode === "view"}
                  filterOptions={createFilter({ ignoreAccents: false })}
                  components={{
                    MenuList,
                  }}
                  className={projectNumberError}
                />
              </div>
              <div className={styles.formField}>
                <label htmlFor="supervisor">Supervisor</label>
                <Select
                  id="supervisor"
                  value={
                    currentSafetyAudit.supervisor
                      ? {
                          value: currentSafetyAudit.supervisor,
                          label: currentSafetyAudit.supervisor_name,
                        }
                      : null
                  }
                  options={employees.map((employee) => ({
                    value: employee.employee_id,
                    label: employee.label,
                  }))}
                  onChange={(selected) =>
                    safetyAuditFormActions.updateSupervisor(selected, emitPatch)
                  }
                  placeholder={
                    (supervisorAutosaveError || submissionErrors.supervisor) ? (
                      <span className={styles.errorText}>
                        {supervisorAutosaveError || submissionErrors.supervisor}
                      </span>
                    ) : (
                      "Select Supervisor"
                    )
                  }
                  isSearchable={true}
                  isClearable={true}
                  styles={{
                    ...delSelect,
                    ...(touchSelectProps.styles || {}),
                  }}
                  menuPortalTarget={touchSelectProps.menuPortalTarget}
                  menuPosition={touchSelectProps.menuPosition}
                  isDisabled={safetyAuditMode === "view"}
                  components={{
                    MenuList,
                  }}
                  className={supervisorError}
                />
              </div>{" "}
              <div
                className={styles.formField}
                style={{ gridColumn: "1 / -1" }}
              >
                <label htmlFor="scopeOfWork">Scope of Work</label>
                <textarea
                  className={`${styles.textareaField} ${scopeOfWorkError}`}
                  id="scopeOfWork"
                  value={currentSafetyAudit.scope_of_work || ""}
                  onChange={(e) =>
                    safetyAuditFormActions.updateScopeOfWork(e.target.value, null)
                  }
                  onBlur={(e) =>
                    safetyAuditFormActions.updateScopeOfWork(e.target.value, emitPatch)
                  }
                  disabled={safetyAuditMode === "view"}
                  rows={3}
                />
                {(scopeOfWorkAutosaveError || submissionErrors.scope_of_work) && (
                  <span className={styles.errorText}>
                    {scopeOfWorkAutosaveError || submissionErrors.scope_of_work}
                  </span>
                )}
              </div>
            </div>
          </div>
          {/* Employees Present Section */}
          <div className={styles.formSection}>
            <h2 className={styles.sectionTitle}>Employees on Site</h2>
            {employeesError && (
              <div className={styles.errorMessage}>
                {typeof employeesError === "string"
                  ? employeesError
                  : "Please fix employee errors below"}
              </div>
            )}
            <div className={styles.employeeList}>
              {currentSafetyAudit.employees.map((employee, index) => {
                // Find errors for this specific employee
                const employeeErrors = Array.isArray(submissionErrors.employees)
                  ? submissionErrors.employees.find(
                      (err) =>
                        err?.safetyAuditChecklistEmployeeRowId === employee.id
                    )?.errors || {}
                  : {};

                const employeeIdError = employeeErrors.employeeId
                  ? styles.errorSelectContainer
                  : "";

                return (
                  <div
                    key={employee.id}
                    className={`${styles.employeeRow} ${
                      employeeErrors.employeeId ? styles.errorEmployeeCard : ""
                    }`}
                  >
                    <div className={styles.employeeField}>
                      <label htmlFor={`employee-${employee.id}`}>
                        Employee {index + 1}
                      </label>
                      <Select
                        id={`employee-${employee.id}`}
                        value={
                          employee.employee_id
                            ? {
                                value: employee.employee_id,
                                label: employee.employee_name,
                              }
                            : null
                        }
                        options={employees.map((emp) => ({
                          value: emp.employee_id,
                          label: emp.label,
                        }))}
                        onChange={(selected) =>
                          safetyAuditFormActions.updateEmployee(
                            employee.id,
                            selected,
                            emitPatch
                          )
                        }
                        placeholder={
                          employeeErrors.employeeId ? (
                            <span className={styles.errorText}>
                              {employeeErrors.employeeId}
                            </span>
                          ) : (
                            "Select Employee"
                          )
                        }
                        isSearchable={true}
                        isClearable={true}
                        styles={{
                          ...delSelect,
                          ...(touchSelectProps.styles || {}),
                        }}
                        menuPortalTarget={touchSelectProps.menuPortalTarget}
                        menuPosition={touchSelectProps.menuPosition}
                        isDisabled={safetyAuditMode === "view"}
                        components={{
                          MenuList,
                        }}
                        className={employeeIdError}
                      />
                      {employeeErrors.employeeId && (
                        <span className={styles.errorText}>
                          {employeeErrors.employeeId}
                        </span>
                      )}
                    </div>
                    <div className={styles.employeeCardActions}>
                      <button
                        type="button"
                        onClick={() =>
                          safetyAuditFormActions.removeEmployee(
                            employee.id,
                            emitPatch
                          )
                        }
                        className={styles.removeEmployeeButton}
                        disabled={safetyAuditMode === "view"}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className={styles.addEmployeeButtonContainer}>
              {safetyAuditMode !== "view" && (
                <button
                  type="button"
                  onClick={() =>
                    safetyAuditFormActions.addNewBlankEmployee(emitPatch)
                  }
                  className={styles.addEmployeeButton}
                  disabled={safetyAuditMode === "view"}
                >
                  Add Employee
                </button>
              )}
            </div>
          </div>

          {/* Paper Work Section */}
          <div className={styles.formSection}>
            <h2 className={styles.sectionTitle}>Paper Work</h2>
            <div className={styles.checkboxGrid}>
              <div
                className={`${styles.checkboxItem} ${
                  submissionErrors.pw_jsa ? styles.error : ""
                }`}
              >
                <label htmlFor="pwJsa">JSA included and completed?</label>
                <input
                  type="checkbox"
                  id="pwJsa"
                  checked={currentSafetyAudit.pw_jsa}
                  onChange={(e) =>
                    safetyAuditFormActions.updatePwJsa(
                      e.target.checked,
                      emitPatch
                    )
                  }
                  disabled={safetyAuditMode === "view"}
                />
              </div>
              {submissionErrors.pw_jsa && (
                <span className={styles.errorText}>{submissionErrors.pw_jsa}</span>
              )}

              <div
                className={`${styles.checkboxItem} ${
                  submissionErrors.pw_field_notes ? styles.error : ""
                }`}
              >
                <label htmlFor="pwFieldNotes">Field Notes included?</label>
                <input
                  type="checkbox"
                  id="pwFieldNotes"
                  checked={currentSafetyAudit.pw_field_notes}
                  onChange={(e) =>
                    safetyAuditFormActions.updatePwFieldNotes(
                      e.target.checked,
                      emitPatch
                    )
                  }
                  disabled={safetyAuditMode === "view"}
                />
              </div>
              {submissionErrors.pw_field_notes && (
                <span className={styles.errorText}>
                  {submissionErrors.pw_field_notes}
                </span>
              )}

              <div
                className={`${styles.checkboxItem} ${
                  submissionErrors.jsa_signed ? styles.error : ""
                }`}
              >
                <label htmlFor="jsaSigned">Has everyone signed the JSA?</label>
                <input
                  type="checkbox"
                  id="jsaSigned"
                  checked={currentSafetyAudit.jsa_signed}
                  onChange={(e) =>
                    safetyAuditFormActions.updateJsaSigned(
                      e.target.checked,
                      emitPatch
                    )
                  }
                  disabled={safetyAuditMode === "view"}
                />
              </div>
              {submissionErrors.jsa_signed && (
                <span className={styles.errorText}>{submissionErrors.jsa_signed}</span>
              )}
            </div>

            <div className={styles.formField} style={{ marginTop: "1rem" }}>
              <label htmlFor="pwComments">Comments</label>
              <textarea
                className={`${styles.textareaField} ${
                  (pwCommentsAutosaveError || submissionErrors.pw_comments) ? styles.error : ""
                }`}
                id="pwComments"
                value={currentSafetyAudit.pw_comments || ""}
                onChange={(e) =>
                  safetyAuditFormActions.updatePwComments(e.target.value, null)
                }
                onBlur={(e) =>
                  safetyAuditFormActions.updatePwComments(e.target.value, emitPatch)
                }
                disabled={safetyAuditMode === "view"}
                rows={3}
              />
              {(pwCommentsAutosaveError || submissionErrors.pw_comments) && (
                <span className={styles.errorText}>
                  {pwCommentsAutosaveError || submissionErrors.pw_comments}
                </span>
              )}
            </div>
          </div>

          {/* Site Observations Section */}
          <div className={styles.formSection}>
            <h2 className={styles.sectionTitle}>Site Observations</h2>
            <div className={styles.formGrid}>
              <div
                className={styles.formField}
                style={{ gridColumn: "1 / -1" }}
              >
                <label htmlFor="equipmentOnSite">
                  List the equipment on site:
                </label>
                <textarea
                  className={`${styles.textareaField} ${
                    (equipmentOnSiteAutosaveError || submissionErrors.equipment_on_site) ? styles.error : ""
                  }`}
                  id="equipmentOnSite"
                  value={currentSafetyAudit.equipment_on_site || ""}
                  onChange={(e) =>
                    safetyAuditFormActions.updateEquipmentOnSite(e.target.value, null)
                  }
                  onBlur={(e) =>
                    safetyAuditFormActions.updateEquipmentOnSite(e.target.value, emitPatch)
                  }
                  disabled={safetyAuditMode === "view"}
                  rows={3}
                />
                {(equipmentOnSiteAutosaveError || submissionErrors.equipment_on_site) && (
                  <span className={styles.errorText}>
                    {equipmentOnSiteAutosaveError || submissionErrors.equipment_on_site}
                  </span>
                )}
              </div>

              <div
                className={styles.formField}
                style={{ gridColumn: "1 / -1" }}
              >
                <label htmlFor="hazardsPresent">
                  List the hazards present:
                </label>
                <textarea
                  className={`${styles.textareaField} ${
                    (hazardsPresentAutosaveError || submissionErrors.hazards_present) ? styles.error : ""
                  }`}
                  id="hazardsPresent"
                  value={currentSafetyAudit.hazards_present || ""}
                  onChange={(e) =>
                    safetyAuditFormActions.updateHazardsPresent(e.target.value, null)
                  }
                  onBlur={(e) =>
                    safetyAuditFormActions.updateHazardsPresent(e.target.value, emitPatch)
                  }
                  disabled={safetyAuditMode === "view"}
                  rows={3}
                />
                {(hazardsPresentAutosaveError || submissionErrors.hazards_present) && (
                  <span className={styles.errorText}>
                    {hazardsPresentAutosaveError || submissionErrors.hazards_present}
                  </span>
                )}
              </div>

              <div className={styles.formField}>
                <label>Hazards listed above are on the JSA?</label>
                {submissionErrors.hazards_on_jsa && (
                  <div className={styles.errorMessage}>
                    {submissionErrors.hazards_on_jsa}
                  </div>
                )}
                <div className={styles.radioGroup}>
                  <div className={styles.radioItem}>
                    <input
                      type="checkbox"
                      id="hazardsOnJsaYes"
                      checked={currentSafetyAudit.hazards_on_jsa_yes}
                      onChange={(e) =>
                        safetyAuditFormActions.updateHazardsOnJsaYes(
                          e.target.checked,
                          emitPatch
                        )
                      }
                    />
                    <label htmlFor="hazardsOnJsaYes">Yes</label>
                  </div>
                  <div className={styles.radioItem}>
                    <input
                      type="checkbox"
                      id="hazardsOnJsaNo"
                      checked={currentSafetyAudit.hazards_on_jsa_no}
                      onChange={(e) =>
                        safetyAuditFormActions.updateHazardsOnJsaNo(
                          e.target.checked,
                          emitPatch
                        )
                      }
                    />
                    <label htmlFor="hazardsOnJsaNo">No</label>
                  </div>
                </div>
              </div>

              <div
                className={styles.formField}
                style={{ gridColumn: "1 / -1" }}
              >
                <label htmlFor="engineeringControls">
                  What engineering controls are being used to mitigate the
                  hazards?
                </label>
                <textarea
                  className={`${styles.textareaField} ${
                    (engineeringControlsAutosaveError || submissionErrors.engineering_controls) ? styles.error : ""
                  }`}
                  id="engineeringControls"
                  value={currentSafetyAudit.engineering_controls || ""}
                  onChange={(e) =>
                    safetyAuditFormActions.updateEngineeringControls(e.target.value, null)
                  }
                  onBlur={(e) =>
                    safetyAuditFormActions.updateEngineeringControls(e.target.value, emitPatch)
                  }
                  disabled={safetyAuditMode === "view"}
                  rows={3}
                />
                {(engineeringControlsAutosaveError || submissionErrors.engineering_controls) && (
                  <span className={styles.errorText}>
                    {engineeringControlsAutosaveError || submissionErrors.engineering_controls}
                  </span>
                )}
              </div>

              <div
                className={styles.formField}
                style={{ gridColumn: "1 / -1" }}
              >
                <label htmlFor="ppeUsed">What PPE is being used?</label>
                <textarea
                  className={`${styles.textareaField} ${
                    (ppeUsedAutosaveError || submissionErrors.ppe_used) ? styles.error : ""
                  }`}
                  id="ppeUsed"
                  value={currentSafetyAudit.ppe_used || ""}
                  onChange={(e) =>
                    safetyAuditFormActions.updatePpeUsed(e.target.value, null)
                  }
                  onBlur={(e) =>
                    safetyAuditFormActions.updatePpeUsed(e.target.value, emitPatch)
                  }
                  disabled={safetyAuditMode === "view"}
                  rows={3}
                />
                {(ppeUsedAutosaveError || submissionErrors.ppe_used) && (
                  <span className={styles.errorText}>
                    {ppeUsedAutosaveError || submissionErrors.ppe_used}
                  </span>
                )}
              </div>

              <div className={styles.formField}>
                <label>Is this the correct PPE?</label>
                {submissionErrors.correct_ppe && (
                  <div className={styles.errorMessage}>
                    {submissionErrors.correct_ppe}
                  </div>
                )}
                <div className={styles.radioGroup}>
                  <div className={styles.radioItem}>
                    <input
                      type="checkbox"
                      id="correctPpeYes"
                      checked={currentSafetyAudit.correct_ppe_yes}
                      onChange={(e) =>
                        safetyAuditFormActions.updateCorrectPpeYes(
                          e.target.checked,
                          emitPatch
                        )
                      }
                    />
                    <label htmlFor="correctPpeYes">Yes</label>
                  </div>
                  <div className={styles.radioItem}>
                    <input
                      type="checkbox"
                      id="correctPpeNo"
                      checked={currentSafetyAudit.correct_ppe_no}
                      onChange={(e) =>
                        safetyAuditFormActions.updateCorrectPpeNo(
                          e.target.checked,
                          emitPatch
                        )
                      }
                    />
                    <label htmlFor="correctPpeNo">No</label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Training & Certifications Section */}
          <div className={styles.formSection}>
            <h2 className={styles.sectionTitle}>Training & Certifications</h2>
            <div className={styles.formGrid}>
              <div className={styles.formField}>
                <label>Is everyone properly trained?</label>
                {submissionErrors.properly_trained && (
                  <div className={styles.errorMessage}>
                    {submissionErrors.properly_trained}
                  </div>
                )}
                <div className={styles.radioGroup}>
                  <div className={styles.radioItem}>
                    <input
                      type="checkbox"
                      id="properlyTrainedYes"
                      checked={currentSafetyAudit.properly_trained_yes}
                      onChange={(e) =>
                        safetyAuditFormActions.updateProperlyTrainedYes(
                          e.target.checked,
                          emitPatch
                        )
                      }
                    />
                    <label htmlFor="properlyTrainedYes">Yes</label>
                  </div>
                  <div className={styles.radioItem}>
                    <input
                      type="checkbox"
                      id="properlyTrainedNo"
                      checked={currentSafetyAudit.properly_trained_no}
                      onChange={(e) =>
                        safetyAuditFormActions.updateProperlyTrainedNo(
                          e.target.checked,
                          emitPatch
                        )
                      }
                    />
                    <label htmlFor="properlyTrainedNo">No</label>
                  </div>
                </div>
              </div>

              <div
                className={styles.formField}
                style={{ gridColumn: "1 / -1" }}
              >
                <label htmlFor="certsObtained">
                  What certs do the employees have? (Have employees pull up
                  their GSDU app and verify all training is up to date)
                </label>
                <textarea
                  className={`${styles.textareaField} ${
                    (certsObtainedAutosaveError || submissionErrors.certs_obtained) ? styles.error : ""
                  }`}
                  id="certsObtained"
                  value={currentSafetyAudit.certs_obtained || ""}
                  onChange={(e) =>
                    safetyAuditFormActions.updateCertsObtained(e.target.value, null)
                  }
                  onBlur={(e) =>
                    safetyAuditFormActions.updateCertsObtained(e.target.value, emitPatch)
                  }
                  disabled={safetyAuditMode === "view"}
                  rows={3}
                />
                {(certsObtainedAutosaveError || submissionErrors.certs_obtained) && (
                  <span className={styles.errorText}>
                    {certsObtainedAutosaveError || submissionErrors.certs_obtained}
                  </span>
                )}
              </div>

              <div
                className={styles.formField}
                style={{ gridColumn: "1 / -1" }}
              >
                <label htmlFor="properlyTrainedComments">
                  Any additional comments about training?
                </label>
                <textarea
                  className={`${styles.textareaField} ${
                    (properlyTrainedCommentsAutosaveError || submissionErrors.properly_trained_comments) ? styles.error : ""
                  }`}
                  id="properlyTrainedComments"
                  value={currentSafetyAudit.properly_trained_comments || ""}
                  onChange={(e) =>
                    safetyAuditFormActions.updateProperlyTrainedComments(e.target.value, null)
                  }
                  onBlur={(e) =>
                    safetyAuditFormActions.updateProperlyTrainedComments(e.target.value, emitPatch)
                  }
                  disabled={safetyAuditMode === "view"}
                  rows={3}
                />
                {(properlyTrainedCommentsAutosaveError || submissionErrors.properly_trained_comments) && (
                  <span className={styles.errorText}>
                    {properlyTrainedCommentsAutosaveError || submissionErrors.properly_trained_comments}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Findings Section */}
          <div className={styles.formSection}>
            <h2 className={styles.sectionTitle}>Findings</h2>
            <div className={styles.formGrid}>
              <div
                className={styles.formField}
                style={{ gridColumn: "1 / -1" }}
              >
                <label htmlFor="positiveFindings">Positive Findings</label>
                <textarea
                  className={`${styles.textareaField} ${
                    (positiveFindingsAutosaveError || submissionErrors.positive_findings) ? styles.error : ""
                  }`}
                  id="positiveFindings"
                  value={currentSafetyAudit.positive_findings || ""}
                  onChange={(e) =>
                    safetyAuditFormActions.updatePositiveFindings(e.target.value, null)
                  }
                  onBlur={(e) =>
                    safetyAuditFormActions.updatePositiveFindings(e.target.value, emitPatch)
                  }
                  disabled={safetyAuditMode === "view"}
                  rows={4}
                />
                {(positiveFindingsAutosaveError || submissionErrors.positive_findings) && (
                  <span className={styles.errorText}>
                    {positiveFindingsAutosaveError || submissionErrors.positive_findings}
                  </span>
                )}
              </div>

              <div
                className={styles.formField}
                style={{ gridColumn: "1 / -1" }}
              >
                <label htmlFor="negativeFindings">Negative Findings</label>
                <textarea
                  className={`${styles.textareaField} ${
                    (negativeFindingsAutosaveError || submissionErrors.negative_findings) ? styles.error : ""
                  }`}
                  id="negativeFindings"
                  value={currentSafetyAudit.negative_findings || ""}
                  onChange={(e) =>
                    safetyAuditFormActions.updateNegativeFindings(e.target.value, null)
                  }
                  onBlur={(e) =>
                    safetyAuditFormActions.updateNegativeFindings(e.target.value, emitPatch)
                  }
                  disabled={safetyAuditMode === "view"}
                  rows={4}
                />
                {(negativeFindingsAutosaveError || submissionErrors.negative_findings) && (
                  <span className={styles.errorText}>
                    {negativeFindingsAutosaveError || submissionErrors.negative_findings}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      ) : (
        <PhotosComponent
          photos={currentSafetyAudit.photos || []}
          photosUI={photosUI}
          photosActions={safetyAuditFormActions.photos}
          emitPatch={emitPatch}
          isViewMode={safetyAuditMode === "view"}
          connectionStatus={autosave.connectionStatus || "connected"}
          canDownload={true}
          formId={currentSafetyAudit.id}
          formType="safety_audit"
          showLoading={showLoading}
          hideLoading={hideLoading}
          showNotification={showNotification}
        />
      )}

      {/* Unsaved photos confirmation modal */}
      <ConfirmationModal
        isOpen={showUnsavedPhotosConfirm}
        onCancel={() => setShowUnsavedPhotosConfirm(false)}
        onConfirm={async () => {
          setShowUnsavedPhotosConfirm(false);
          await executeSubmitSafetyAudit();
        }}
        title="Unsaved Photos"
        message={`You have ${photosUI?.selectedPhotos?.length || 0} selected photo(s) that have not been saved. If you continue, these photos will not be included.`}
        confirmText="Continue Without Photos"
        cancelText="Go Back"
      />

      {/* Action Buttons Container */}
      <div className={styles.actionButtonsContainer}>
        <div className={styles.rightSideTableActions}>
          <BackToTableButton
            backToTableFunction={handleBackToTable}
            tableName="Safety Audit"
          />
          {shouldAutosave && (
            <AutosaveIndicator
              connectionStatus={autosave.connectionStatus}
              saveStatus={autosave.saveStatus}
              pendingCount={autosave.pendingCount}
              lastError={autosave.lastError}
              onErrorClick={(error) => {
                showNotification({
                  type: "error",
                  title: error.summary || "Autosave Error",
                  message: error.field_errors
                    ? Object.entries(error.field_errors)
                        .map(([field, msg]) => `${field}: ${msg}`)
                        .join("\n")
                    : "An error occurred while saving.",
                });
              }}
            />
          )}
        </div>

        <div className={styles.leftSideTableActions}>
          {safetyAuditMode !== "view" && safetyAuditMode !== "approve" && (
            <SubmitSafetyFormButton
              submitFunction={handleSubmitSafetyAudit}
            />
          )}
        </div>
      </div>
    </div>
  );
};
